import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'

const DEFAULT_INTERVAL = 20000

type Value<T> = {
  count: number
  data?: T
}

type IdType = number | string

type GetData<T> = (ids: IdType[], setData: (id: IdType, data?: T) => void) => void

type Options<T> = {
  getData?: GetData<T>;
  intervalMs?: number;
}

export default function createUsePollingWithCache<T>({
  getData,
  intervalMs = DEFAULT_INTERVAL,
}: Options<T> = {}) {
  let _getData = getData
  let _intervalMs = intervalMs

  let _cache = new Map<IdType, Value<T>>()
  let _intervalRef: ReturnType<typeof setInterval> | undefined
  let _intervalHandler = () => {}

  function reducer (cache: Map<IdType, Value<T>>, action) {
    switch (action.type) {
      case 'registerId':
        if (cache.has(action.id)) {
          return new Map(cache).set(action.id, {
            ...cache.get(action.id),
            count: cache.get(action.id)!.count + 1,
          })
        } else {
          return new Map(cache).set(action.id, {
            count: 1,
          })
        }
      case 'unregisterId':
        if (cache.has(action.id)) {
          return new Map(cache).set(action.id, {
            ...cache.get(action.id),
            count: cache.get(action.id)!.count - 1,
          })
        } else {
          return cache
        }
      case 'setData':
        if (cache.has(action.id)) {
          return new Map(cache).set(action.id, {
            count: 0,
            ...cache.get(action.id),
            data: action.data,
          })
        } else {
          return cache
        }
      default:
        throw new Error()
    }
  }

  const listeners = new Set<(cache: Map<IdType, Value<T>>) => void>()

  const getDataAwaitingIds = new Set<IdType>()
  let fetchTimeout: ReturnType<typeof setTimeout> | undefined

  const queueGetData = (ids: IdType[]) => {
    ids.forEach(id => getDataAwaitingIds.add(id))

    if (!fetchTimeout) {
      fetchTimeout = setTimeout(() => {
        _getData?.(Array.from(getDataAwaitingIds), (id, data) => dispatch({ type: 'setData', id, data }))
  
        getDataAwaitingIds.clear()
  
        fetchTimeout = undefined
      }, 10)
    }
  }

  const onChange = (cache: Map<IdType, Value<T>>) => {
    const prevCache = _cache

    listeners.forEach(listener => listener(cache))
    _cache = cache

    const ids = Array.from(cache.keys()).filter(id => Boolean(cache.get(id)?.count))
    const prevIds = Array.from(prevCache.keys()).filter(id => Boolean(prevCache.get(id)?.count))

    const newIds = ids.filter(id => !prevIds.includes(id))
    if (newIds.length > 0) {
      queueGetData(newIds)
    }

    if (ids.length > 0) {
      _intervalHandler = () => {
        queueGetData(ids)
      }

      if (!_intervalRef && _intervalMs) {
        _intervalRef = setInterval(() => _intervalHandler(), _intervalMs)
      }
    } else {
      _intervalHandler = () => {}

      if (_intervalRef) {
        clearInterval(_intervalRef)
        _intervalRef = undefined
      }
    }
  }

  const updateIntervalMs = (interval: number) => {
    if (interval !== _intervalMs) {
      _intervalMs = interval

      if (_intervalRef) {
        clearInterval(_intervalRef)

        if (_intervalMs) {
          _intervalRef = setInterval(() => _intervalHandler(), _intervalMs)
        }
      }
    }
  }

  const dispatch = (actionOrActions: object | object[]) => {
    let newCache

    if (Array.isArray(actionOrActions)) {
      newCache = actionOrActions.reduce(reducer, _cache)
    } else {
      newCache = reducer(_cache, actionOrActions)
    }

    if (newCache !== _cache) {
      onChange(newCache)
    }
  }
  
  function usePollingWithCache (id: string, options?: Options<T>): T;
  function usePollingWithCache (ids: string[], options?: Options<T>): T[];
  function usePollingWithCache (id: number, options?: Options<T>): T;
  function usePollingWithCache (ids: number[], options?: Options<T>): T[];
  function usePollingWithCache (idOrIds: string | number | (string | number)[], options?: Options<T>) {
    const [cacheData, setCacheData] = useState(_cache)

    const prevIdsRef = useRef<IdType[]>([])

    useEffect(() => {
      if (options) {
        if (options.getData) _getData = options.getData
        if (options.intervalMs !== undefined) {
          updateIntervalMs(options.intervalMs)
        }
      }
    }, [options])

    useEffect(() => {
      listeners.add(setCacheData)

      return () => {
        listeners.delete(setCacheData)
      }
    }, [])

    useEffect(() => {
      let ids: IdType[]

      if (Array.isArray(idOrIds)) {
        ids = idOrIds
      } else {
        ids = [idOrIds]
      }

      const addedIds = _.difference<IdType>(ids, prevIdsRef.current)
      const removedIds = _.difference<IdType>(prevIdsRef.current, ids)

      if (addedIds.length > 0 || removedIds.length > 0) {
        dispatch([
          ...addedIds.map(id =>
            ({ type: 'registerId', id })
          ),
          ...removedIds.map(id =>
            ({ type: 'unregisterId', id })
          ),
        ])
      }

      prevIdsRef.current = ids
    }, [idOrIds])

    useEffect(() => {
      return () => {
        dispatch(prevIdsRef.current.map(id =>
          ({ type: 'unregisterId', id })
        ))
      }
    }, [])

    if (Array.isArray(idOrIds)) {
      return idOrIds.map(id => cacheData.get(id)?.data)
    } else {
      return cacheData.get(idOrIds)?.data
    }
  }

  return usePollingWithCache
}


